import { Injectable, inject } from '@angular/core';
import { Observable, finalize, map, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { ApiCallService } from './api-call-service.service';

/**
 * The "reconnect and replace token" flow, shared by the websocket (on every connection) and the
 * 401 interceptor:
 *   1. User/RefreshToken?UserId=<userId>                      -> refreshed token
 *   2. ConnectionToken/GenerateConnectionToken?token=<token>  -> new token
 *   3. the token returned by step 2 replaces localStorage['token'].
 * Only one run is ever in flight - callers that arrive meanwhile share its result.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private api = inject(ApiCallService);
  private inFlight$: Observable<string> | null = null;

  get isRefreshing(): boolean {
    return this.inFlight$ !== null;
  }

  refresh(): Observable<string> {
    if (this.inFlight$) return this.inFlight$;

    const userId = typeof localStorage !== 'undefined' ? localStorage.getItem('userId') : null;
    if (!userId) return throwError(() => new Error('No userId - cannot refresh token'));

    this.inFlight$ = this.api.GetCallWithoutToken('User/RefreshToken?UserId=' + userId).pipe(
      switchMap((refreshResponse: any) => {
        const refreshedToken = refreshResponse?.data?.token;
        if (refreshResponse?.responseCode !== 200 || !refreshedToken) {
          return throwError(() => new Error('RefreshToken failed'));
        }
        // Keep the Bearer header valid for the next call
        localStorage.setItem('token', refreshedToken);
        return this.api.GetCallWithToken(
          `ConnectionToken/GenerateConnectionToken?token=${encodeURIComponent(refreshedToken)}`,
        );
      }),
      map((response: any) => {
        const newToken =
          response?.Token ?? response?.token ?? response?.data?.Token ?? response?.data?.token;
        if (typeof newToken !== 'string' || !newToken) {
          throw new Error('GenerateConnectionToken returned no token');
        }
        return newToken;
      }),
      tap((newToken) => localStorage.setItem('token', newToken)),
      finalize(() => (this.inFlight$ = null)),
      shareReplay(1),
    );
    return this.inFlight$;
  }
}
